import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, MapPin, CreditCard, Package, FileText, Settings, Save, Check, Building2, Truck, Eye, Download, Clock, CheckCircle, AlertCircle, Receipt, DollarSign, PenTool, MessageSquare, ThumbsUp, X, Printer, RefreshCw, ChevronDown } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { QuotePreviewModal } from '../components/QuotePreviewModal';
import { ChangeRequestDialog } from '../components/ChangeRequestDialog';
import { formatPrice } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ReceiptsList } from '../components/dashboard/ReceiptsList';
import { AccountPasswordSection } from '../components/account/AccountPasswordSection';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Business type labels for display
const BUSINESS_TYPE_LABELS = {
  'interior-designer': 'Interior Designer',
  'architect': 'Architect',
  'builder-developer': 'Builder / Developer',
  'builder': 'Builder / Developer',
  'contractor': 'Contractor',
  'dealer-reseller': 'Dealer / Reseller',
  'dealer': 'Dealer / Reseller',
  'custom-av-integrator': 'Custom AV Integrator',
};

// Quote status configuration for client view
const QUOTE_STATUS_CONFIG = {
  'pending': { label: 'Pending Review', color: 'bg-amber-100 text-amber-700', needsAction: false },
  'awaiting_approval': { label: 'Awaiting Your Approval', color: 'bg-blue-100 text-blue-700', needsAction: true },
  'changes_requested': { label: 'Changes Requested', color: 'bg-purple-100 text-purple-700', needsAction: false },
  'client_approved': { label: 'Approved - Awaiting Signature', color: 'bg-green-100 text-green-700', needsAction: true },
  'signed': { label: 'Signed', color: 'bg-emerald-100 text-emerald-700', needsAction: false },
  'approved': { label: 'Approved', color: 'bg-green-100 text-green-700', needsAction: false },
  'production': { label: 'In Production', color: 'bg-indigo-100 text-indigo-700', needsAction: false },
  'completed': { label: 'Completed', color: 'bg-neutral-100 text-neutral-700', needsAction: false },
  'cancelled': { label: 'Cancelled', color: 'bg-red-100 text-red-700', needsAction: false },
};

// Order status configuration
const ORDER_STATUSES = {
  'confirmed': { label: 'Order Confirmed', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  'paid': { label: 'Payment Received', color: 'bg-green-100 text-green-700', icon: DollarSign },
  'production': { label: 'In Production', color: 'bg-amber-100 text-amber-700', icon: Clock },
  'ready': { label: 'Ready for Pickup/Ship', color: 'bg-purple-100 text-purple-700', icon: Package },
  'shipped': { label: 'Shipped', color: 'bg-indigo-100 text-indigo-700', icon: Truck },
  'delivered': { label: 'Delivered', color: 'bg-green-100 text-green-700', icon: CheckCircle },
};

export const DashboardPage = () => {
  const { user, token, loading: authLoading, updateUser, updateToken } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [orders, setOrders] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedSalesOrder, setSelectedSalesOrder] = useState(null);
  const [st120Data, setSt120Data] = useState(null);
  
  // Quote approval workflow states
  const [showQuoteActionModal, setShowQuoteActionModal] = useState(null); // 'approve', 'changes', 'sign'
  const [actionQuote, setActionQuote] = useState(null);
  const [changeRequestMessage, setChangeRequestMessage] = useState('');
  const [signatureName, setSignatureName] = useState('');
  const [signatureAgreed, setSignatureAgreed] = useState(false);
  const [processingAction, setProcessingAction] = useState(false);
  
  // Sales Order change request dialog state
  const [showSOChangeDialog, setShowSOChangeDialog] = useState(false);
  const [changeRequestSO, setChangeRequestSO] = useState(null);
  const [submittingChangeRequest, setSubmittingChangeRequest] = useState(false);
  
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
  });
  const [shippingAddress, setShippingAddress] = useState({
    jobsite_name: '',
    street: '',
    unit: '',
    city: '',
    state: '',
    zip: '',
    contact_name: '',
    contact_phone: '',
    delivery_instructions: '',
  });
  const [billingAddress, setBillingAddress] = useState({
    street: '',
    unit: '',
    city: '',
    state: '',
    zip: '',
    same_as_shipping: false,
  });

  useEffect(() => {
    if (token) {
      fetchProfile();
      fetchOrders();
      fetchQuotes();
      fetchInvoices();
      fetchSalesOrders();
      fetchST120();
    }
  }, [token]);

  const fetchSalesOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/sales-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSalesOrders(response.data || []);
    } catch (error) {
      console.error('Failed to fetch sales orders:', error);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/user/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      setProfile({
        name: data.name || '',
        email: data.email || '',
        phone: data.phone || '',
        company: data.company || '',
      });
      if (data.shipping_address) {
        setShippingAddress(data.shipping_address);
      }
      if (data.billing_address) {
        setBillingAddress(data.billing_address);
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/user/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    }
  };

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API_URL}/quotes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotes(response.data);
    } catch (error) {
      console.error('Failed to fetch quotes:', error);
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API_URL}/user/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    }
  };

  const fetchST120 = async () => {
    try {
      const response = await axios.get(`${API_URL}/user/st120`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSt120Data(response.data);
    } catch (error) {
      // ST-120 not submitted yet, that's okay
      console.log('No ST-120 on file');
    }
  };

  const handleDownloadPDF = async (quoteId) => {
    try {
      const response = await axios.get(`${API_URL}/quotes/${quoteId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `apollo-quote-${quoteId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handlePrintQuote = async (quoteId) => {
    try {
      const response = await axios.get(`${API_URL}/quotes/${quoteId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } catch (error) {
      toast.error('Failed to print quote');
    }
  };

  const handlePrintInvoice = async (invoiceId) => {
    try {
      const response = await axios.get(`${API_URL}/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } catch (error) {
      toast.error('Failed to print invoice');
    }
  };

  const handlePrintSalesOrder = async (soId) => {
    try {
      const response = await axios.get(`${API_URL}/sales-orders/${soId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } catch (error) {
      toast.error('Failed to print sales order');
    }
  };

  const handleDownloadInvoicePDF = async (invoiceId, invoiceNumber) => {
    try {
      const response = await axios.get(`${API_URL}/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `apollo-invoice-${invoiceNumber || invoiceId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download invoice PDF');
    }
  };

  const handleDownloadSalesOrderPDF = async (soId, soNumber) => {
    try {
      const response = await axios.get(`${API_URL}/sales-orders/${soId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `apollo-so-${soNumber || soId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download sales order PDF');
    }
  };

  // Quote action handlers
  const openQuoteAction = (quote, action) => {
    setActionQuote(quote);
    setShowQuoteActionModal(action);
    setChangeRequestMessage('');
    setSignatureName(profile.name || user.name || '');
    setSignatureAgreed(false);
  };

  const closeQuoteAction = () => {
    setShowQuoteActionModal(null);
    setActionQuote(null);
    setChangeRequestMessage('');
    setSignatureAgreed(false);
  };

  const handleApproveQuote = async () => {
    if (!actionQuote) return;
    setProcessingAction(true);
    try {
      await axios.post(`${API_URL}/quotes/${actionQuote.id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Quote approved! Please sign to confirm your order.');
      fetchQuotes();
      closeQuoteAction();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve quote');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleRequestChanges = async () => {
    if (!actionQuote || !changeRequestMessage.trim()) {
      toast.error('Please describe the changes you need');
      return;
    }
    setProcessingAction(true);
    try {
      await axios.post(`${API_URL}/quotes/${actionQuote.id}/request-changes`, {
        message: changeRequestMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Change request submitted. We\'ll update your quote shortly.');
      fetchQuotes();
      closeQuoteAction();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit change request');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleSignQuote = async () => {
    if (!actionQuote) return;
    if (!signatureName.trim()) {
      toast.error('Please enter your name for the signature');
      return;
    }
    if (!signatureAgreed) {
      toast.error('Please agree to the terms to proceed');
      return;
    }
    setProcessingAction(true);
    try {
      await axios.post(`${API_URL}/quotes/${actionQuote.id}/sign`, {
        signer_name: signatureName,
        agreed_to_terms: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Quote signed successfully! Your order has been confirmed.');
      fetchQuotes();
      closeQuoteAction();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign quote');
    } finally {
      setProcessingAction(false);
    }
  };

  const getQuoteStatusConfig = (status) => {
    return QUOTE_STATUS_CONFIG[status] || QUOTE_STATUS_CONFIG['pending'];
  };

  const quotesNeedingAction = quotes.filter(q => 
    q.status === 'awaiting_approval' || q.status === 'client_approved'
  );

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/user/profile`, {
        ...profile,
        shipping_address: shippingAddress,
        billing_address: billingAddress.same_as_shipping ? {
          ...shippingAddress,
          same_as_shipping: true
        } : billingAddress,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile updated successfully');
      if (updateUser) {
        updateUser({ ...user, ...profile });
      }
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  // Show loading while auth is being checked
  if (authLoading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <div className="min-h-screen pt-32 pb-16">
          <div className="container-lg text-center">
            <User className="w-16 h-16 mx-auto text-neutral-300 mb-6" />
            <h1 className="text-2xl font-serif mb-4">Sign In Required</h1>
            <p className="text-neutral-600 mb-6">Please sign in to access your dashboard.</p>
            <Button onClick={() => window.location.href = '/login'} className="btn-primary">
              Sign In
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Back Button */}
      <div className="container-xl pt-24 pb-2">
        <button 
          onClick={() => window.history.back()} 
          className="inline-flex items-center text-neutral-500 hover:text-neutral-900 transition-colors"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
          Back
        </button>
      </div>

      {/* Hero */}
      <section className="pt-8 pb-8 bg-neutral-50">
        <div className="container-xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="label-overline text-amber-700">My Account</span>
            <h1 className="mt-4 text-neutral-900">Dashboard</h1>
            <p className="mt-2 text-neutral-600">Welcome back, {profile.name || user.name}</p>
          </motion.div>
        </div>
      </section>

      {/* Dashboard Content */}
      <section className="section-padding">
        <div className="container-xl">
          <Tabs defaultValue="profile" className="space-y-8">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex flex-col gap-2 items-start">
                {/* Row 1: Profile, Addresses, Documents (only for trade) */}
                <TabsList className="bg-neutral-100 p-1 rounded-lg">
                  <TabsTrigger value="profile" className="flex items-center gap-2 data-[state=active]:bg-white">
                    <User className="w-4 h-4" /> Profile
                  </TabsTrigger>
                  <TabsTrigger value="addresses" className="flex items-center gap-2 data-[state=active]:bg-white">
                    <MapPin className="w-4 h-4" /> Addresses
                  </TabsTrigger>
                  {user.is_trade && (
                    <TabsTrigger value="documents" className="flex items-center gap-2 data-[state=active]:bg-white">
                      <FileText className="w-4 h-4" /> Tax & Trade Documents
                    </TabsTrigger>
                  )}
                </TabsList>
                {/* Row 2: Quotes, SO, Invoices, Shipping - aligned left */}
                <TabsList className="bg-neutral-100 p-1 rounded-lg">
                  <TabsTrigger value="quotes" className="flex items-center gap-2 data-[state=active]:bg-white">
                    <FileText className="w-4 h-4" /> Quotes
                  </TabsTrigger>
                  <TabsTrigger value="orders" className="flex items-center gap-2 data-[state=active]:bg-white">
                    <Package className="w-4 h-4" /> Sales Orders
                  </TabsTrigger>
                  <TabsTrigger value="invoices" className="flex items-center gap-2 data-[state=active]:bg-white">
                    <Receipt className="w-4 h-4" /> Invoices
                  </TabsTrigger>
                  <TabsTrigger value="shipping" className="flex items-center gap-2 data-[state=active]:bg-white">
                    <Truck className="w-4 h-4" /> Shipping
                  </TabsTrigger>
                </TabsList>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  fetchProfile();
                  fetchQuotes();
                  fetchOrders();
                  fetchInvoices();
                  fetchST120();
                  toast.success('Dashboard refreshed');
                }}
                className="flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" /> Refresh
              </Button>
            </div>

            {/* Profile Tab */}
            <TabsContent value="profile">
              <div className="bg-white border border-neutral-200 rounded-lg p-6 max-w-2xl">
                <h2 className="text-lg font-semibold mb-6">Profile Information</h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Full Name *</Label>
                      <Input
                        value={profile.name}
                        onChange={(e) => setProfile({...profile, name: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Email Address *</Label>
                      <Input
                        type="email"
                        value={profile.email}
                        onChange={(e) => setProfile({...profile, email: e.target.value})}
                        className="mt-1"
                        disabled
                      />
                      <p className="text-xs text-neutral-500 mt-1">Contact support to change email</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Phone Number</Label>
                      <Input
                        type="tel"
                        value={profile.phone}
                        onChange={(e) => setProfile({...profile, phone: e.target.value})}
                        placeholder="(555) 123-4567"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Company Name</Label>
                      <Input
                        value={profile.company}
                        onChange={(e) => setProfile({...profile, company: e.target.value})}
                        placeholder="Your business name"
                        className="mt-1"
                      />
                    </div>
                  </div>

                  {/* Trade Account Status */}
                  <div className="mt-6 p-4 bg-neutral-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">Trade Account Status</h4>
                        <p className="text-sm text-neutral-500">
                          {user.is_trade ? 'You have trade pricing enabled' : 'Apply for trade pricing'}
                        </p>
                        {/* Show business type if trade account */}
                        {user.is_trade && (user.business_type || user.trade_application?.business_type) && (
                          <div className="mt-2 flex items-center gap-2">
                            <span className="text-xs text-neutral-400">Account Type:</span>
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded text-xs font-medium">
                              {BUSINESS_TYPE_LABELS[user.business_type || user.trade_application?.business_type] || (user.business_type || user.trade_application?.business_type)}
                            </span>
                          </div>
                        )}
                      </div>
                      {user.is_trade ? (
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                          Active
                        </span>
                      ) : (
                        <Link to="/trade">
                          <Button variant="outline" size="sm">Apply Now</Button>
                        </Link>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button onClick={saveProfile} disabled={saving} className="btn-primary">
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </div>
              </div>
              <AccountPasswordSection token={token} onNewToken={updateToken} className="bg-white border border-neutral-200 rounded-lg p-6 max-w-2xl mt-6" />
            </TabsContent>

            {/* Addresses Tab */}
            <TabsContent value="addresses">
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Shipping Address (Jobsite) */}
                <div className="bg-white border border-neutral-200 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Truck className="w-5 h-5 text-amber-700" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold">Shipping Address</h2>
                      <p className="text-sm text-neutral-500">Jobsite / Delivery Location</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <Label>Jobsite / Project Name</Label>
                      <Input
                        value={shippingAddress.jobsite_name}
                        onChange={(e) => setShippingAddress({...shippingAddress, jobsite_name: e.target.value})}
                        placeholder="e.g., Smith Residence, Downtown Office"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Street Address *</Label>
                      <Input
                        value={shippingAddress.street}
                        onChange={(e) => setShippingAddress({...shippingAddress, street: e.target.value})}
                        placeholder="123 Main Street"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Apt / Suite / Unit</Label>
                      <Input
                        value={shippingAddress.unit}
                        onChange={(e) => setShippingAddress({...shippingAddress, unit: e.target.value})}
                        placeholder="Suite 100"
                        className="mt-1"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>City *</Label>
                        <Input
                          value={shippingAddress.city}
                          onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label>State *</Label>
                          <Input
                            value={shippingAddress.state}
                            onChange={(e) => setShippingAddress({...shippingAddress, state: e.target.value})}
                            className="mt-1"
                            maxLength={2}
                          />
                        </div>
                        <div>
                          <Label>ZIP *</Label>
                          <Input
                            value={shippingAddress.zip}
                            onChange={(e) => setShippingAddress({...shippingAddress, zip: e.target.value})}
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Site Contact Name</Label>
                        <Input
                          value={shippingAddress.contact_name}
                          onChange={(e) => setShippingAddress({...shippingAddress, contact_name: e.target.value})}
                          placeholder="Site manager name"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Site Contact Phone</Label>
                        <Input
                          value={shippingAddress.contact_phone}
                          onChange={(e) => setShippingAddress({...shippingAddress, contact_phone: e.target.value})}
                          placeholder="(555) 123-4567"
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Delivery Instructions</Label>
                      <textarea
                        value={shippingAddress.delivery_instructions}
                        onChange={(e) => setShippingAddress({...shippingAddress, delivery_instructions: e.target.value})}
                        placeholder="Gate code, loading dock info, best time to deliver..."
                        rows={3}
                        className="w-full mt-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Billing Address */}
                <div className="bg-white border border-neutral-200 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-neutral-100 rounded-lg flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-neutral-700" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold">Billing Address</h2>
                      <p className="text-sm text-neutral-500">For invoices and receipts</p>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <label className="flex items-center gap-3 p-3 bg-neutral-50 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={billingAddress.same_as_shipping}
                        onChange={(e) => setBillingAddress({...billingAddress, same_as_shipping: e.target.checked})}
                        className="rounded"
                      />
                      <span className="text-sm">Same as shipping address</span>
                    </label>

                    {!billingAddress.same_as_shipping && (
                      <>
                        <div>
                          <Label>Street Address *</Label>
                          <Input
                            value={billingAddress.street}
                            onChange={(e) => setBillingAddress({...billingAddress, street: e.target.value})}
                            placeholder="123 Main Street"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Apt / Suite / Unit</Label>
                          <Input
                            value={billingAddress.unit}
                            onChange={(e) => setBillingAddress({...billingAddress, unit: e.target.value})}
                            placeholder="Suite 100"
                            className="mt-1"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>City *</Label>
                            <Input
                              value={billingAddress.city}
                              onChange={(e) => setBillingAddress({...billingAddress, city: e.target.value})}
                              className="mt-1"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label>State *</Label>
                              <Input
                                value={billingAddress.state}
                                onChange={(e) => setBillingAddress({...billingAddress, state: e.target.value})}
                                className="mt-1"
                                maxLength={2}
                              />
                            </div>
                            <div>
                              <Label>ZIP *</Label>
                              <Input
                                value={billingAddress.zip}
                                onChange={(e) => setBillingAddress({...billingAddress, zip: e.target.value})}
                                className="mt-1"
                              />
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <Button onClick={saveProfile} disabled={saving} className="btn-primary">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Addresses'}
                </Button>
              </div>
            </TabsContent>

            {/* Quotes Tab */}
            <TabsContent value="quotes">
              <div className="space-y-6">
                {/* Quotes Needing Action Alert */}
                {quotesNeedingAction.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                        <AlertCircle className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-amber-800">Action Required</h3>
                        <p className="text-sm text-amber-700">
                          You have {quotesNeedingAction.length} quote{quotesNeedingAction.length > 1 ? 's' : ''} awaiting your response
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-white border border-neutral-200 rounded-lg">
                  <div className="p-6 border-b border-neutral-200">
                    <h2 className="text-lg font-semibold">My Quotes</h2>
                    <p className="text-sm text-neutral-500">Review, approve, and sign your quotes</p>
                  </div>
                  
                  {quotes.length === 0 ? (
                    <div className="p-12 text-center">
                      <FileText className="w-12 h-12 mx-auto text-neutral-300 mb-4" />
                      <h3 className="text-lg font-medium mb-2">No quotes yet</h3>
                      <p className="text-neutral-500 mb-6">Configure your products and request a quote</p>
                      <Link to="/products">
                        <Button className="btn-primary">Browse Products</Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="divide-y divide-neutral-200">
                      {quotes.map((quote) => {
                        const statusConfig = getQuoteStatusConfig(quote.status);
                        const needsApproval = quote.status === 'awaiting_approval';
                        const needsSignature = quote.status === 'client_approved';
                        
                        return (
                          <div key={quote.id} className={`p-6 ${statusConfig.needsAction ? 'bg-amber-50/30' : 'hover:bg-neutral-50'}`}>
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="font-mono font-medium">Quote #{quote.id?.slice(0, 8).toUpperCase()}</p>
                                  <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${statusConfig.color}`}>
                                    {statusConfig.label}
                                  </span>
                                  {quote.signature && (
                                    <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-emerald-100 text-emerald-700 flex items-center gap-1">
                                      <PenTool className="w-3 h-3" /> Signed
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-neutral-500 mt-1">
                                  {new Date(quote.created_at).toLocaleDateString()} • {quote.items?.length || 0} items
                                </p>
                                
                                {/* Status Flow Timeline - Hover to reveal */}
                                <div className="mt-4 group relative">
                                  <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-neutral-100 hover:bg-neutral-200 rounded-lg cursor-pointer transition-colors">
                                    <div className="flex items-center gap-1">
                                      {/* Mini progress dots */}
                                      <div className="w-2 h-2 rounded-full bg-green-500" />
                                      <div className={`w-2 h-2 rounded-full ${['client_approved', 'signed', 'converted_to_so', 'converted', 'invoiced'].includes(quote.status) ? 'bg-green-500' : 'bg-neutral-300'}`} />
                                      <div className={`w-2 h-2 rounded-full ${['converted_to_so', 'converted', 'invoiced'].includes(quote.status) ? 'bg-green-500' : 'bg-neutral-300'}`} />
                                      <div className={`w-2 h-2 rounded-full ${quote.production_status === 'completed' ? 'bg-green-500' : quote.production_status === 'in_production' ? 'bg-amber-500' : 'bg-neutral-300'}`} />
                                      <div className={`w-2 h-2 rounded-full ${['delivered', 'picked_up'].includes(quote.fulfillment_status) ? 'bg-green-500' : 'bg-neutral-300'}`} />
                                    </div>
                                    <span className="text-xs font-medium text-neutral-600">Order Progress</span>
                                    <ChevronDown className="w-3 h-3 text-neutral-400 group-hover:rotate-180 transition-transform" />
                                  </div>
                                  
                                  {/* Expanded timeline on hover */}
                                  <div className="absolute left-0 top-full mt-2 z-20 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 bg-white border border-neutral-200 rounded-lg shadow-lg p-4 w-full max-w-[900px]">
                                    <p className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-4">Order Progress</p>
                                    
                                    {/* Timeline Container */}
                                    <div className="relative">
                                      {/* Main timeline with steps */}
                                      <div className="flex items-center">
                                        
                                        {/* Step 1: Created */}
                                        {(() => {
                                          const createdByAdmin = quote.created_by_role === 'admin' || quote.created_by_name === 'Sales Team';
                                          const creatorName = createdByAdmin ? 'Sales Team' : (quote.created_by_name || '');
                                          return (
                                            <div className="flex flex-col items-center">
                                              <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                                                <Check className="w-4 h-4 text-white" />
                                              </div>
                                              <span className="text-xs font-medium text-green-700 mt-1">Created</span>
                                              {creatorName && (
                                                <span className="text-xs text-neutral-500">by {creatorName}</span>
                                              )}
                                            </div>
                                          );
                                        })()}
                                        
                                        {/* Connector 1-2: Approved (Quote Approval) */}
                                        {(() => {
                                          const isApproved = ['client_approved', 'signed', 'converted_to_so', 'converted', 'invoiced'].includes(quote.status);
                                          const isPending = quote.status === 'awaiting_approval';
                                          const approvedByAdmin = quote.approved_by_role === 'admin' || quote.approved_by === 'admin' || quote.approved_by === 'Admin' || quote.approved_by === 'Sales Team';
                                          const approverName = quote.approved_by 
                                            ? (approvedByAdmin ? 'Sales Team' : quote.approved_by)
                                            : (isApproved && quote.created_by_role === 'admin' ? 'Sales Team' : (isApproved ? (quote.customer_name || 'Customer') : ''));
                                          return (
                                            <div className="flex-1 flex flex-col items-center mx-1">
                                              <span className={`text-xs font-medium mb-1 ${isApproved ? 'text-green-600' : isPending ? 'text-amber-600' : 'text-neutral-400'}`}>
                                                {isApproved ? '✓ Approved' : isPending ? '⏳ Pending' : 'Approved'}
                                              </span>
                                              <div className={`w-full h-1 rounded ${isApproved ? 'bg-green-500' : isPending ? 'bg-amber-400' : 'bg-neutral-300'}`} />
                                              {isApproved && approverName && (
                                                <span className="text-xs text-green-600 mt-1">
                                                  by {approverName}
                                                </span>
                                              )}
                                            </div>
                                          );
                                        })()}
                                        
                                        {/* Step 2: Sales Order */}
                                        {(() => {
                                          const hasSO = quote.status === 'converted_to_so' || quote.sales_order_id || ['converted', 'invoiced'].includes(quote.status);
                                          const soCreatedByAdmin = quote.so_created_by_role === 'admin';
                                          const soCreatorName = hasSO 
                                            ? (soCreatedByAdmin ? 'Sales Team' : (quote.so_created_by || ''))
                                            : '';
                                          return (
                                            <div className="flex flex-col items-center">
                                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${hasSO ? 'bg-green-500' : 'bg-neutral-300'}`}>
                                                {hasSO ? <Check className="w-4 h-4 text-white" /> : <span className="text-white text-xs font-bold">2</span>}
                                              </div>
                                              <span className={`text-xs font-medium mt-1 ${hasSO ? 'text-green-700' : 'text-neutral-400'}`}>Sales Order</span>
                                              {hasSO && soCreatorName && (
                                                <span className="text-xs text-neutral-500">by {soCreatorName}</span>
                                              )}
                                            </div>
                                          );
                                        })()}
                                        
                                        {/* Connector 2-3: SO Approved */}
                                        {(() => {
                                          const hasSO = quote.status === 'converted_to_so' || quote.sales_order_id || ['converted', 'invoiced'].includes(quote.status);
                                          const soIsApproved = quote.so_approved || quote.so_approved_at || ['converted', 'invoiced'].includes(quote.status);
                                          const soApprovedByAdmin = quote.so_approved_by_role === 'admin' || quote.so_approved_by === 'admin' || quote.so_approved_by === 'Admin' || quote.so_approved_by === 'Sales Team';
                                          const soApproverName = soIsApproved 
                                            ? (soApprovedByAdmin ? 'Sales Team' : (quote.so_approved_by || quote.customer_name || 'Customer'))
                                            : '';
                                          return (
                                            <div className="flex-1 flex flex-col items-center mx-1">
                                              <span className={`text-xs font-medium mb-1 ${soIsApproved ? 'text-green-600' : hasSO ? 'text-amber-600' : 'text-neutral-400'}`}>
                                                {soIsApproved ? '✓ Approved' : hasSO ? '⏳ Pending' : 'Approved'}
                                              </span>
                                              <div className={`w-full h-1 rounded ${soIsApproved ? 'bg-green-500' : hasSO ? 'bg-amber-400' : 'bg-neutral-300'}`} />
                                              {soIsApproved && soApproverName && (
                                                <span className="text-xs text-green-600 mt-1">
                                                  by {soApproverName}
                                                </span>
                                              )}
                                            </div>
                                          );
                                        })()}
                                        
                                        {/* Step 3: Invoiced */}
                                        {(() => {
                                          const hasInvoice = quote.status === 'invoiced' || quote.status === 'converted' || quote.invoice_id;
                                          return (
                                            <div className="flex flex-col items-center">
                                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${hasInvoice ? 'bg-green-500' : 'bg-neutral-300'}`}>
                                                {hasInvoice ? <Check className="w-4 h-4 text-white" /> : <span className="text-white text-xs font-bold">3</span>}
                                              </div>
                                              <span className={`text-xs font-medium mt-1 ${hasInvoice ? 'text-green-700' : 'text-neutral-400'}`}>Invoiced</span>
                                            </div>
                                          );
                                        })()}
                                        
                                        {/* Connector 3-4: Paid */}
                                        {(() => {
                                          const isPaid = quote.payment_status === 'paid' || quote.invoice_paid;
                                          const isPartial = quote.payment_status === 'partial';
                                          return (
                                            <div className="flex-1 flex flex-col items-center mx-1">
                                              <span className={`text-xs font-medium mb-1 ${isPaid ? 'text-green-600' : isPartial ? 'text-amber-600' : 'text-neutral-400'}`}>
                                                {isPaid ? '✓ Paid' : isPartial ? '⏳ Partial' : 'Paid'}
                                              </span>
                                              <div className={`w-full h-1 rounded ${isPaid ? 'bg-green-500' : isPartial ? 'bg-amber-400' : 'bg-neutral-300'}`} />
                                              <span className="text-xs text-neutral-500 mt-1">
                                                {isPaid && quote.paid_at ? new Date(quote.paid_at).toLocaleDateString() : ''}
                                              </span>
                                            </div>
                                          );
                                        })()}
                                        
                                        {/* Step 4: In Production */}
                                        {(() => {
                                          const isComplete = quote.production_status === 'completed';
                                          const isInProgress = quote.production_status === 'in_production';
                                          return (
                                            <div className="flex flex-col items-center">
                                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                                isComplete ? 'bg-green-500' : isInProgress ? 'bg-amber-500' : 'bg-neutral-300'
                                              }`}>
                                                {isComplete ? <Check className="w-4 h-4 text-white" /> : <span className="text-white text-xs font-bold">4</span>}
                                              </div>
                                              <span className={`text-xs font-medium mt-1 ${
                                                isComplete ? 'text-green-700' : isInProgress ? 'text-amber-700' : 'text-neutral-400'
                                              }`}>Production</span>
                                            </div>
                                          );
                                        })()}
                                        
                                        {/* Connector 4-5: Fulfillment */}
                                        {(() => {
                                          const isDelivered = ['delivered', 'picked_up'].includes(quote.fulfillment_status);
                                          const isShipped = ['shipped', 'in_transit', 'ready_for_pickup'].includes(quote.fulfillment_status);
                                          const fulfillmentType = quote.fulfillment_type || quote.fulfillment_method || 'shipping';
                                          const fulfillmentLabel = fulfillmentType === 'pickup' ? 'Pick-up' :
                                                                    fulfillmentType === 'delivery' ? 'Local Delivery' : 'Shipped';
                                          return (
                                            <div className="flex-1 flex flex-col items-center mx-1">
                                              <span className={`text-xs font-medium mb-1 ${isDelivered ? 'text-green-600' : isShipped ? 'text-blue-600' : 'text-neutral-400'}`}>
                                                {isDelivered ? '✓ Fulfillment' : isShipped ? '🚚 Fulfillment' : 'Fulfillment'}
                                              </span>
                                              <div className={`w-full h-1 rounded ${isDelivered ? 'bg-green-500' : isShipped ? 'bg-blue-500' : 'bg-neutral-300'}`} />
                                              <span className="text-xs text-neutral-500 mt-1">
                                                {(isShipped || isDelivered) ? fulfillmentLabel : ''}
                                              </span>
                                            </div>
                                          );
                                        })()}
                                        
                                        {/* Step 5: Delivered */}
                                        {(() => {
                                          const isDelivered = ['delivered', 'picked_up'].includes(quote.fulfillment_status);
                                          const isShipped = ['shipped', 'in_transit', 'ready_for_pickup'].includes(quote.fulfillment_status);
                                          return (
                                            <div className="flex flex-col items-center">
                                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                                isDelivered ? 'bg-green-500' : isShipped ? 'bg-blue-500' : 'bg-neutral-300'
                                              }`}>
                                                {isDelivered ? <Check className="w-4 h-4 text-white" /> : <span className="text-white text-xs font-bold">5</span>}
                                              </div>
                                              <span className={`text-xs font-medium mt-1 ${
                                                isDelivered ? 'text-green-700' : 'text-neutral-400'
                                              }`}>{quote.fulfillment_status === 'picked_up' ? 'Picked Up' : 'Delivered'}</span>
                                            </div>
                                          );
                                        })()}
                                        
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Change request message if exists */}
                                {quote.change_requests?.length > 0 && (
                                  <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                                    <p className="text-xs font-medium text-purple-700 mb-1">Your change request:</p>
                                    <p className="text-sm text-purple-800">{quote.change_requests[quote.change_requests.length - 1].message}</p>
                                  </div>
                                )}
                                
                                {/* Signature info if signed */}
                                {quote.signature && (
                                  <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                                    <p className="text-xs text-emerald-700">
                                      Signed by <strong>{quote.signature.signer_name}</strong> on {new Date(quote.signature.signed_at).toLocaleDateString()}
                                    </p>
                                  </div>
                                )}
                              </div>
                              
                              <div className="flex items-start gap-4 ml-4">
                                <div className="text-right">
                                  <p className="font-mono font-medium text-lg">{formatPrice(quote.total)}</p>
                                  <p className="text-xs text-neutral-500">{quote.items?.length || 0} items</p>
                                </div>
                                
                                <div className="flex flex-col gap-2">
                                  {/* Action buttons based on status */}
                                  {needsApproval && (
                                    <>
                                      <Button
                                        size="sm"
                                        className="btn-primary"
                                        onClick={() => openQuoteAction(quote, 'approve')}
                                      >
                                        <ThumbsUp className="w-4 h-4 mr-1" /> Approve
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => openQuoteAction(quote, 'changes')}
                                      >
                                        <MessageSquare className="w-4 h-4 mr-1" /> Request Changes
                                      </Button>
                                    </>
                                  )}
                                  
                                  {needsSignature && (
                                    <Button
                                      size="sm"
                                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                      onClick={() => openQuoteAction(quote, 'sign')}
                                    >
                                      <PenTool className="w-4 h-4 mr-1" /> Sign Quote
                                    </Button>
                                  )}
                                  
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setSelectedQuote(quote)}
                                  >
                                    <Eye className="w-4 h-4 mr-1" /> View
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDownloadPDF(quote.id)}
                                  >
                                    <Download className="w-4 h-4 mr-1" /> PDF
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handlePrintQuote(quote.id)}
                                  >
                                    <Printer className="w-4 h-4 mr-1" /> Print
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Quote Preview Modal */}
              <AnimatePresence>
                {selectedQuote && (
                  <QuotePreviewModal
                    quote={selectedQuote}
                    onClose={() => setSelectedQuote(null)}
                    onDownload={() => handleDownloadPDF(selectedQuote.id)}
                    onPrint={() => handlePrintQuote(selectedQuote.id)}
                  />
                )}
              </AnimatePresence>

              {/* Quote Action Modals */}
              <AnimatePresence>
                {showQuoteActionModal && actionQuote && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                    onClick={closeQuoteAction}
                  >
                    <motion.div
                      initial={{ scale: 0.95, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.95, opacity: 0 }}
                      className="bg-white rounded-xl max-w-lg w-full"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Approve Quote Modal */}
                      {showQuoteActionModal === 'approve' && (
                        <div>
                          <div className="p-6 border-b border-neutral-200">
                            <h2 className="text-lg font-semibold flex items-center gap-2">
                              <ThumbsUp className="w-5 h-5 text-green-600" />
                              Approve Quote
                            </h2>
                            <p className="text-sm text-neutral-500 mt-1">
                              Quote #{actionQuote.id?.slice(0, 8).toUpperCase()}
                            </p>
                          </div>
                          <div className="p-6">
                            <div className="bg-neutral-50 rounded-lg p-4 mb-6">
                              <div className="flex justify-between items-center">
                                <span className="text-neutral-600">Total Amount</span>
                                <span className="font-mono font-bold text-xl">{formatPrice(actionQuote.total)}</span>
                              </div>
                            </div>
                            <p className="text-sm text-neutral-600 mb-6">
                              By approving this quote, you confirm that the products, quantities, and pricing are correct. 
                              After approval, you'll need to sign the quote to confirm your order.
                            </p>
                            <div className="flex gap-3">
                              <Button variant="outline" onClick={closeQuoteAction} className="flex-1">
                                Cancel
                              </Button>
                              <Button 
                                onClick={handleApproveQuote} 
                                disabled={processingAction}
                                className="flex-1 btn-primary"
                              >
                                {processingAction ? 'Approving...' : 'Approve Quote'}
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Request Changes Modal */}
                      {showQuoteActionModal === 'changes' && (
                        <div>
                          <div className="p-6 border-b border-neutral-200">
                            <h2 className="text-lg font-semibold flex items-center gap-2">
                              <MessageSquare className="w-5 h-5 text-purple-600" />
                              Request Changes
                            </h2>
                            <p className="text-sm text-neutral-500 mt-1">
                              Quote #{actionQuote.id?.slice(0, 8).toUpperCase()}
                            </p>
                          </div>
                          <div className="p-6">
                            <Label className="text-sm font-medium">What changes do you need?</Label>
                            <Textarea
                              value={changeRequestMessage}
                              onChange={(e) => setChangeRequestMessage(e.target.value)}
                              placeholder="Please describe the changes you'd like us to make to this quote..."
                              rows={4}
                              className="mt-2"
                            />
                            <p className="text-xs text-neutral-500 mt-2">
                              Our team will review your request and update the quote accordingly.
                            </p>
                            <div className="flex gap-3 mt-6">
                              <Button variant="outline" onClick={closeQuoteAction} className="flex-1">
                                Cancel
                              </Button>
                              <Button 
                                onClick={handleRequestChanges} 
                                disabled={processingAction || !changeRequestMessage.trim()}
                                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
                              >
                                {processingAction ? 'Submitting...' : 'Submit Request'}
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Sign Quote Modal */}
                      {showQuoteActionModal === 'sign' && (
                        <div>
                          <div className="p-6 border-b border-neutral-200">
                            <h2 className="text-lg font-semibold flex items-center gap-2">
                              <PenTool className="w-5 h-5 text-emerald-600" />
                              Sign Quote & Confirm Order
                            </h2>
                            <p className="text-sm text-neutral-500 mt-1">
                              Quote #{actionQuote.id?.slice(0, 8).toUpperCase()}
                            </p>
                          </div>
                          <div className="p-6">
                            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6">
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-emerald-700 font-medium">Order Total</span>
                                <span className="font-mono font-bold text-2xl text-emerald-800">{formatPrice(actionQuote.total)}</span>
                              </div>
                              <p className="text-xs text-emerald-600">{actionQuote.items?.length || 0} items</p>
                            </div>

                            <div className="space-y-4">
                              <div>
                                <Label className="text-sm font-medium">Your Full Name (Electronic Signature)</Label>
                                <Input
                                  value={signatureName}
                                  onChange={(e) => setSignatureName(e.target.value)}
                                  placeholder="Enter your full legal name"
                                  className="mt-2"
                                />
                              </div>

                              <div className="bg-neutral-50 rounded-lg p-4">
                                <label className="flex items-start gap-3 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={signatureAgreed}
                                    onChange={(e) => setSignatureAgreed(e.target.checked)}
                                    className="mt-1 rounded"
                                  />
                                  <span className="text-sm text-neutral-700">
                                    I agree to the terms and conditions. By signing, I authorize Apollo Shades to proceed 
                                    with the order as specified in this quote. I understand that this constitutes a 
                                    legally binding agreement.
                                  </span>
                                </label>
                              </div>

                              {signatureName && signatureAgreed && (
                                <div className="border-2 border-dashed border-emerald-300 rounded-lg p-4 bg-emerald-50">
                                  <p className="text-xs text-emerald-600 mb-1">Electronic Signature Preview</p>
                                  <p className="font-signature text-2xl text-emerald-800 italic">{signatureName}</p>
                                  <p className="text-xs text-emerald-600 mt-2">
                                    Date: {new Date().toLocaleDateString()} • IP logged for verification
                                  </p>
                                </div>
                              )}
                            </div>

                            <div className="flex gap-3 mt-6">
                              <Button variant="outline" onClick={closeQuoteAction} className="flex-1">
                                Cancel
                              </Button>
                              <Button 
                                onClick={handleSignQuote} 
                                disabled={processingAction || !signatureName.trim() || !signatureAgreed}
                                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                              >
                                {processingAction ? 'Signing...' : 'Sign & Confirm Order'}
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </TabsContent>

            {/* Orders Tab */}
            <TabsContent value="orders">
              <div className="bg-white border border-neutral-200 rounded-lg">
                <div className="p-6 border-b border-neutral-200">
                  <h2 className="text-lg font-semibold">Sales Orders</h2>
                  <p className="text-sm text-neutral-500">View and approve your sales orders</p>
                </div>
                
                {/* Pending Approval Alert */}
                {salesOrders.filter(so => so.status === 'pending_customer_approval' || (so.status === 'pending_approval' && !so.customer_approved)).length > 0 && (
                  <div className="mx-6 mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                    <div>
                      <p className="font-medium text-amber-800">Action Required</p>
                      <p className="text-sm text-amber-700">
                        You have {salesOrders.filter(so => so.status === 'pending_customer_approval' || (so.status === 'pending_approval' && !so.customer_approved)).length} sales order(s) awaiting your approval
                      </p>
                    </div>
                  </div>
                )}
                
                {salesOrders.length === 0 ? (
                  <div className="p-12 text-center">
                    <Package className="w-12 h-12 mx-auto text-neutral-300 mb-4" />
                    <h3 className="text-lg font-medium mb-2">No sales orders yet</h3>
                    <p className="text-neutral-500 mb-6">Sales orders will appear here after quote approval</p>
                  </div>
                ) : (
                  <div className="divide-y divide-neutral-200">
                    {salesOrders.map((so) => {
                      const statusColors = {
                        draft: 'bg-neutral-100 text-neutral-700',
                        pending_admin_approval: 'bg-blue-100 text-blue-700',
                        pending_customer_approval: 'bg-amber-100 text-amber-700',
                        pending_approval: 'bg-amber-100 text-amber-700',
                        approved: 'bg-green-100 text-green-700',
                        changes_requested: 'bg-red-100 text-red-700',
                        converted_to_invoice: 'bg-green-100 text-green-700'
                      };
                      const statusLabels = {
                        draft: 'Draft',
                        pending_admin_approval: 'Awaiting Admin Approval',
                        pending_customer_approval: 'Awaiting Your Approval',
                        pending_approval: 'Awaiting Your Approval',
                        approved: 'Fully Approved',
                        changes_requested: 'Changes Requested',
                        converted_to_invoice: 'Invoiced'
                      };
                      
                      // Determine if customer approval is needed
                      const needsCustomerApproval = so.status === 'pending_customer_approval' || 
                        (so.status === 'pending_approval' && !so.customer_approved);
                      
                      return (
                        <div key={so.id} className="p-6 hover:bg-neutral-50">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 flex-wrap">
                                <p className="font-mono font-medium">{so.so_number}</p>
                                <span className={`px-2 py-1 text-xs rounded-full font-medium ${statusColors[so.status] || 'bg-neutral-100'}`}>
                                  {statusLabels[so.status] || so.status}
                                </span>
                                {/* Dual approval indicators */}
                                <div className="flex items-center gap-2 text-xs">
                                  <span className={`flex items-center gap-0.5 ${so.admin_approved ? 'text-green-600' : 'text-neutral-400'}`}>
                                    {so.admin_approved ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                                    Admin
                                  </span>
                                  <span className={`flex items-center gap-0.5 ${so.customer_approved ? 'text-green-600' : 'text-neutral-400'}`}>
                                    {so.customer_approved ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                                    You
                                  </span>
                                </div>
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${
                                  so.fulfillment_type === 'shipping' ? 'bg-blue-50 text-blue-600' :
                                  so.fulfillment_type === 'pickup' ? 'bg-purple-50 text-purple-600' :
                                  'bg-green-50 text-green-600'
                                }`}>
                                  <Truck className="w-3 h-3" />
                                  {so.fulfillment_type?.charAt(0).toUpperCase() + so.fulfillment_type?.slice(1)}
                                </span>
                              </div>
                              <p className="text-sm text-neutral-500 mt-1">
                                {new Date(so.created_at).toLocaleDateString()} • {so.items?.length || 0} items
                              </p>
                              
                              {/* Admin approved indicator */}
                              {so.admin_approved && !so.customer_approved && (
                                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg inline-flex items-center gap-2">
                                  <CheckCircle className="w-4 h-4 text-green-600" />
                                  <span className="text-sm text-green-700">Approved by Sales Team - Your approval needed</span>
                                </div>
                              )}
                              
                              {/* Change request message */}
                              {so.change_request && so.status === 'changes_requested' && (
                                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                                  <p className="text-xs font-medium text-red-700 mb-1">Your request:</p>
                                  <p className="text-sm text-red-800">{so.change_request}</p>
                                </div>
                              )}
                            </div>
                            
                            <div className="flex items-start gap-4 ml-4">
                              <div className="text-right">
                                <p className="font-mono font-medium text-lg">{formatPrice(so.total)}</p>
                                <p className="text-xs text-neutral-500">{so.items?.length || 0} items</p>
                                {so.tax_exempt && (
                                  <p className="text-xs text-green-600">Tax Exempt</p>
                                )}
                              </div>
                              
                              <div className="flex flex-col gap-2">
                                {/* Action buttons based on status */}
                                {needsCustomerApproval && (
                                  <>
                                    <Button
                                      size="sm"
                                      className="bg-green-600 hover:bg-green-700 text-white"
                                      onClick={async () => {
                                        if (!window.confirm('Approve this sales order?')) return;
                                        try {
                                          const response = await axios.put(`${API_URL}/sales-orders/${so.id}/approve`, {}, {
                                            headers: { Authorization: `Bearer ${token}` }
                                          });
                                          if (response.data.fully_approved) {
                                            toast.success('Sales Order fully approved! Invoice has been generated.');
                                          } else {
                                            toast.success('Your approval recorded. Waiting for admin approval.');
                                          }
                                          fetchSalesOrders();
                                        } catch (error) {
                                          toast.error('Failed to approve');
                                        }
                                      }}
                                      data-testid="customer-approve-so-btn"
                                    >
                                      <CheckCircle className="w-4 h-4 mr-1" /> Approve
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      data-testid="customer-request-changes-btn"
                                      onClick={() => {
                                        setChangeRequestSO(so);
                                        setShowSOChangeDialog(true);
                                      }}
                                    >
                                      <MessageSquare className="w-4 h-4 mr-1" /> Request Changes
                                    </Button>
                                  </>
                                )}
                                
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setSelectedSalesOrder(so)}
                                >
                                  <Eye className="w-4 h-4 mr-1" /> View
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDownloadSalesOrderPDF(so.id, so.so_number)}
                                >
                                  <Download className="w-4 h-4 mr-1" /> PDF
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handlePrintSalesOrder(so.id)}
                                >
                                  <Printer className="w-4 h-4 mr-1" /> Print
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Sales Order Details Modal */}
              {selectedSalesOrder && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedSalesOrder(null)}>
                  <div className="bg-white rounded-lg max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                    {/* Header */}
                    <div className="p-6 border-b border-neutral-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-3">
                            <Package className="w-6 h-6 text-blue-600" />
                            <h2 className="text-xl font-semibold">Sales Order {selectedSalesOrder.so_number}</h2>
                          </div>
                          <p className="text-sm text-neutral-500 mt-1">Created {new Date(selectedSalesOrder.created_at).toLocaleDateString()}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownloadSalesOrderPDF(selectedSalesOrder.id, selectedSalesOrder.so_number)}
                          >
                            <Download className="w-4 h-4 mr-1" /> PDF
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePrintSalesOrder(selectedSalesOrder.id)}
                          >
                            <Printer className="w-4 h-4 mr-1" /> Print
                          </Button>
                          <button onClick={() => setSelectedSalesOrder(null)} className="p-2 hover:bg-neutral-100 rounded-lg">
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-6 space-y-6">
                      {/* Status Badge */}
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                          selectedSalesOrder.status === 'approved' ? 'bg-green-100 text-green-700' :
                          selectedSalesOrder.status === 'pending_approval' ? 'bg-amber-100 text-amber-700' :
                          selectedSalesOrder.status === 'converted_to_invoice' ? 'bg-blue-100 text-blue-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {selectedSalesOrder.status === 'pending_approval' ? 'Awaiting Your Approval' :
                           selectedSalesOrder.status === 'approved' ? 'Approved' :
                           selectedSalesOrder.status === 'converted_to_invoice' ? 'Converted to Invoice' :
                           'Changes Requested'}
                        </span>
                        {selectedSalesOrder.approved_at && (
                          <span className="text-sm text-neutral-500">
                            Approved on {new Date(selectedSalesOrder.approved_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      
                      {/* Customer Info */}
                      <div className="grid grid-cols-2 gap-6">
                        <div className="p-4 bg-neutral-50 rounded-lg">
                          <p className="text-xs text-neutral-500 uppercase tracking-wider mb-2">Customer</p>
                          <p className="font-medium">{selectedSalesOrder.customer_name}</p>
                          {selectedSalesOrder.company_name && (
                            <p className="text-sm text-neutral-600">{selectedSalesOrder.company_name}</p>
                          )}
                          <p className="text-sm text-neutral-600">{selectedSalesOrder.customer_email}</p>
                        </div>
                        <div className={`p-4 rounded-lg ${
                          selectedSalesOrder.fulfillment_type === 'shipping' ? 'bg-blue-50' :
                          selectedSalesOrder.fulfillment_type === 'pickup' ? 'bg-purple-50' :
                          'bg-green-50'
                        }`}>
                          <p className="text-xs text-neutral-500 uppercase tracking-wider mb-2">Fulfillment</p>
                          <div className="flex items-center gap-2 font-medium">
                            {selectedSalesOrder.fulfillment_type === 'shipping' && <Truck className="w-4 h-4" />}
                            {selectedSalesOrder.fulfillment_type === 'pickup' && <MapPin className="w-4 h-4" />}
                            {selectedSalesOrder.fulfillment_type === 'delivery' && <Building2 className="w-4 h-4" />}
                            <span className="capitalize">{selectedSalesOrder.fulfillment_type || 'Shipping'}</span>
                          </div>
                          {selectedSalesOrder.pickup_location && (
                            <p className="text-sm mt-1">{selectedSalesOrder.pickup_location}</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Items Table */}
                      <div>
                        <h3 className="font-medium mb-3">Order Items</h3>
                        <div className="border rounded-lg overflow-hidden">
                          <table className="w-full">
                            <thead className="bg-neutral-100 text-sm">
                              <tr>
                                <th className="text-left px-4 py-3 font-medium">Item</th>
                                <th className="text-left px-4 py-3 font-medium">Specifications</th>
                                <th className="text-center px-4 py-3 font-medium">Qty</th>
                                <th className="text-right px-4 py-3 font-medium">Unit Price</th>
                                <th className="text-right px-4 py-3 font-medium">Amount</th>
                              </tr>
                            </thead>
                            <tbody className="text-sm">
                              {selectedSalesOrder.items?.map((item, idx) => {
                                const config = item.config || {};
                                const productName = (config.product_type || 'Custom Shade').replace(/-/g, ' ');
                                const isDrape = productName.toLowerCase().includes('drape');
                                return (
                                  <tr key={idx} className="border-t">
                                    <td className="px-4 py-3">
                                      <p className="font-medium capitalize">{productName}</p>
                                      {config.room_label && <p className="text-xs text-neutral-500">{config.room_label}</p>}
                                    </td>
                                    <td className="px-4 py-3 text-neutral-600">
                                      <div className="space-y-1">
                                        <p className="font-medium">{config.width}" × {config.height}"</p>
                                        {/* Fabric & Color */}
                                        {config.fabric_material && (
                                          <p className="text-xs">
                                            <span className="text-neutral-400">Fabric:</span> {config.fabric_material}
                                          </p>
                                        )}
                                        {config.fabric_color && (
                                          <p className="text-xs">
                                            <span className="text-neutral-400">Color:</span> {config.fabric_color}
                                          </p>
                                        )}
                                        {config.color && !config.fabric_color && (
                                          <p className="text-xs">
                                            <span className="text-neutral-400">Color:</span> {config.color}
                                          </p>
                                        )}
                                        {/* Drape-specific options */}
                                        {isDrape && config.drape_style && (
                                          <p className="text-xs">
                                            <span className="text-neutral-400">Style:</span> {config.drape_style.replace(/-/g, ' ')}
                                          </p>
                                        )}
                                        {isDrape && config.pleat_style && (
                                          <p className="text-xs">
                                            <span className="text-neutral-400">Pleat:</span> {config.pleat_style.replace(/-/g, ' ')}
                                          </p>
                                        )}
                                        {isDrape && config.header_style && (
                                          <p className="text-xs">
                                            <span className="text-neutral-400">Header:</span> {config.header_style.replace(/-/g, ' ')}
                                          </p>
                                        )}
                                        {/* Control/Operation */}
                                        {config.control_type && (
                                          <p className="text-xs">
                                            <span className="text-neutral-400">Control:</span> {config.control_type.replace(/-/g, ' ')}
                                          </p>
                                        )}
                                        {config.motor_type && (
                                          <p className="text-xs">
                                            <span className="text-neutral-400">Motor:</span> {config.motor_type.replace(/-/g, ' ')}
                                          </p>
                                        )}
                                        {/* Mount type */}
                                        {config.mount_type && (
                                          <p className="text-xs">
                                            <span className="text-neutral-400">Mount:</span> {config.mount_type.replace(/-/g, ' ')}
                                          </p>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-center">{item.quantity}</td>
                                    <td className="px-4 py-3 text-right font-mono">{formatPrice(config.total_price)}</td>
                                    <td className="px-4 py-3 text-right font-mono">{formatPrice(config.total_price)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      
                      {/* Totals */}
                      <div className="border-t pt-4">
                        <div className="flex justify-end">
                          <div className="w-64 space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-neutral-500">Subtotal</span>
                              <span className="font-mono">{formatPrice(selectedSalesOrder.subtotal)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-neutral-500">
                                Tax {selectedSalesOrder.tax_exempt && <span className="text-green-600">(Exempt)</span>}
                              </span>
                              <span className="font-mono">{formatPrice(selectedSalesOrder.tax)}</span>
                            </div>
                            <div className="flex justify-between text-lg font-semibold pt-2 border-t">
                              <span>Total</span>
                              <span className="font-mono">{formatPrice(selectedSalesOrder.total)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Change Request Note */}
                      {selectedSalesOrder.change_request && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-sm font-medium text-red-800">Change Request</p>
                          <p className="text-sm text-red-700 mt-1">{selectedSalesOrder.change_request}</p>
                        </div>
                      )}
                      
                      {/* Action Buttons */}
                      {selectedSalesOrder.status === 'pending_approval' && (
                        <div className="flex gap-3 pt-4 border-t">
                          <Button
                            className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={async () => {
                              if (!window.confirm('Approve this sales order?')) return;
                              try {
                                await axios.put(`${API_URL}/sales-orders/${selectedSalesOrder.id}/approve`, {}, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                toast.success('Sales Order approved!');
                                setSelectedSalesOrder(null);
                                fetchSalesOrders();
                              } catch (error) {
                                toast.error('Failed to approve');
                              }
                            }}
                          >
                            <Check className="w-4 h-4 mr-2" /> Approve Order
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => {
                              const message = window.prompt('Please describe the changes you need:');
                              if (message) {
                                axios.put(`${API_URL}/sales-orders/${selectedSalesOrder.id}/request-changes`, { message }, {
                                  headers: { Authorization: `Bearer ${token}` }
                                }).then(() => {
                                  toast.success('Change request submitted');
                                  setSelectedSalesOrder(null);
                                  fetchSalesOrders();
                                }).catch(() => toast.error('Failed to submit request'));
                              }
                            }}
                          >
                            <MessageSquare className="w-4 h-4 mr-2" /> Request Changes
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Invoices Tab */}
            <TabsContent value="invoices">
              <div className="bg-white border border-neutral-200 rounded-lg">
                <div className="p-6 border-b border-neutral-200">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Receipt className="w-5 h-5 text-amber-600" />
                    My Invoices
                  </h2>
                  <p className="text-sm text-neutral-500">View and pay your invoices</p>
                </div>
                
                {invoices.length === 0 ? (
                  <div className="p-12 text-center">
                    <Receipt className="w-12 h-12 mx-auto text-neutral-300 mb-4" />
                    <h3 className="text-lg font-medium mb-2">No invoices yet</h3>
                    <p className="text-neutral-500">Invoices will appear here once your orders are processed</p>
                  </div>
                ) : (
                  <div className="divide-y divide-neutral-200">
                    {invoices.map((invoice) => (
                      <div key={invoice.id} className="p-4 hover:bg-neutral-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              invoice.status === 'paid' ? 'bg-green-100' : 'bg-amber-100'
                            }`}>
                              <Receipt className={`w-5 h-5 ${
                                invoice.status === 'paid' ? 'text-green-600' : 'text-amber-600'
                              }`} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-mono font-medium">{invoice.invoice_number}</span>
                                {invoice.status === 'paid' ? (
                                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-bold">
                                    PAID
                                  </span>
                                ) : (
                                  <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                                    invoice.status === 'overdue' ? 'bg-red-100 text-red-700' :
                                    invoice.status === 'partial' ? 'bg-amber-100 text-amber-700' :
                                    'bg-blue-100 text-blue-700'
                                  }`}>
                                    {invoice.status?.charAt(0).toUpperCase() + invoice.status?.slice(1)}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-neutral-500">
                                {new Date(invoice.created_at).toLocaleDateString()} • {invoice.items?.length || 0} items
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="font-mono font-medium">{formatPrice(invoice.total)}</p>
                              {invoice.balance_due > 0 && invoice.status !== 'paid' && (
                                <p className="text-xs text-red-600">Balance: {formatPrice(invoice.balance_due)}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setSelectedInvoice(invoice)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadInvoicePDF(invoice.id, invoice.invoice_number)}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePrintInvoice(invoice.id)}
                              >
                                <Printer className="w-4 h-4" />
                              </Button>
                              {invoice.status !== 'paid' && invoice.balance_due > 0 && (
                                <Link to={`/pay/invoice/${invoice.id}`}>
                                  <Button size="sm" className="btn-primary">
                                    <CreditCard className="w-4 h-4 mr-1" /> Pay
                                  </Button>
                                </Link>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Payment Receipts with PDF downloads */}
              <ReceiptsList token={token} />
            </TabsContent>

            {/* Shipping Tab */}
            <TabsContent value="shipping">
              <div className="bg-white border border-neutral-200 rounded-lg p-6">
                <h2 className="text-lg font-semibold flex items-center gap-2 mb-6">
                  <Truck className="w-5 h-5 text-blue-600" />
                  Shipping & Tracking
                </h2>
                
                {(() => {
                  // Filter paid invoices that are in production or fulfillment
                  const trackableInvoices = invoices.filter(inv => 
                    inv.status === 'paid' && 
                    (inv.production_status !== 'pending' || inv.fulfillment_status !== 'pending')
                  );
                  
                  if (trackableInvoices.length === 0) {
                    return (
                      <div className="text-center py-12">
                        <Truck className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                        <p className="text-neutral-500">No shipments to track yet</p>
                        <p className="text-sm text-neutral-400 mt-1">Orders will appear here once payment is received and production begins</p>
                      </div>
                    );
                  }
                  
                  return (
                    <div className="space-y-4">
                      {trackableInvoices.map(invoice => {
                        // Determine overall progress level (0-5)
                        let progress = 0;
                        if (invoice.status === 'paid') progress = 1;
                        if (invoice.production_status === 'in_production') progress = 2;
                        if (invoice.production_status === 'completed') progress = 3;
                        if (['shipped', 'in_transit', 'ready_for_pickup'].includes(invoice.fulfillment_status)) progress = 4;
                        if (['delivered', 'picked_up'].includes(invoice.fulfillment_status)) progress = 5;
                        
                        const isShipping = invoice.fulfillment_type === 'shipping';
                        const isPickup = invoice.fulfillment_type === 'pickup';
                        const isDelivery = invoice.fulfillment_type === 'delivery';
                        
                        return (
                          <div key={invoice.id} className="border border-neutral-200 rounded-lg p-4" data-testid={`shipment-card-${invoice.id}`}>
                            <div className="flex items-center justify-between mb-4">
                              <div>
                                <p className="font-mono font-medium">{invoice.invoice_number}</p>
                                <p className="text-sm text-neutral-500">
                                  {new Date(invoice.created_at).toLocaleDateString()} • {invoice.items?.length || 0} items
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                                  isShipping ? 'bg-blue-50 text-blue-700' :
                                  isPickup ? 'bg-purple-50 text-purple-700' :
                                  'bg-green-50 text-green-700'
                                }`}>
                                  {isShipping && <Truck className="w-3 h-3" />}
                                  {isPickup && <MapPin className="w-3 h-3" />}
                                  {isDelivery && <Building2 className="w-3 h-3" />}
                                  {invoice.fulfillment_type ? invoice.fulfillment_type.charAt(0).toUpperCase() + invoice.fulfillment_type.slice(1) : 'Shipping'}
                                </span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  progress === 5 ? 'bg-green-100 text-green-700' :
                                  progress >= 4 ? 'bg-blue-100 text-blue-700' :
                                  progress >= 2 ? 'bg-amber-100 text-amber-700' :
                                  'bg-neutral-100 text-neutral-700'
                                }`}>
                                  {progress === 5 ? (isPickup ? 'Picked Up' : 'Delivered') :
                                   progress === 4 ? (isPickup ? 'Ready for Pickup' : 'In Transit') :
                                   progress === 3 ? 'Ready to Ship' :
                                   progress === 2 ? 'In Production' :
                                   'Processing'}
                                </span>
                              </div>
                            </div>
                            
                            {/* Progress Steps */}
                            <div className="mb-4">
                              <div className="flex items-center gap-1">
                                {/* Step 1: Paid */}
                                <div className={`w-3 h-3 rounded-full ${progress >= 1 ? 'bg-green-500' : 'bg-neutral-300'}`} />
                                <div className={`flex-1 h-1 ${progress >= 2 ? 'bg-green-500' : 'bg-neutral-200'}`} />
                                {/* Step 2: Production */}
                                <div className={`w-3 h-3 rounded-full ${progress >= 2 ? 'bg-green-500' : 'bg-neutral-300'}`} />
                                <div className={`flex-1 h-1 ${progress >= 3 ? 'bg-green-500' : 'bg-neutral-200'}`} />
                                {/* Step 3: Ready */}
                                <div className={`w-3 h-3 rounded-full ${progress >= 3 ? 'bg-green-500' : 'bg-neutral-300'}`} />
                                <div className={`flex-1 h-1 ${progress >= 4 ? 'bg-green-500' : 'bg-neutral-200'}`} />
                                {/* Step 4: Shipped/Ready for Pickup */}
                                <div className={`w-3 h-3 rounded-full ${progress >= 4 ? 'bg-green-500' : 'bg-neutral-300'}`} />
                                <div className={`flex-1 h-1 ${progress >= 5 ? 'bg-green-500' : 'bg-neutral-200'}`} />
                                {/* Step 5: Delivered */}
                                <div className={`w-3 h-3 rounded-full ${progress >= 5 ? 'bg-green-500' : 'bg-neutral-300'}`} />
                              </div>
                              <div className="flex justify-between text-xs text-neutral-500 mt-2">
                                <span>Paid</span>
                                <span>Production</span>
                                <span>Ready</span>
                                <span>{isPickup ? 'Ready' : 'Shipped'}</span>
                                <span>{isPickup ? 'Picked Up' : 'Delivered'}</span>
                              </div>
                            </div>
                            
                            {/* Tracking Info */}
                            {invoice.tracking_number && (
                              <div className="p-3 bg-blue-50 rounded-lg">
                                <p className="text-sm text-blue-800">
                                  <span className="font-medium">Tracking #:</span> {invoice.tracking_number}
                                </p>
                                {invoice.carrier && (
                                  <p className="text-sm text-blue-800">
                                    <span className="font-medium">Carrier:</span> {invoice.carrier}
                                  </p>
                                )}
                              </div>
                            )}
                            
                            {/* Pickup Location */}
                            {isPickup && invoice.pickup_location && (
                              <div className="p-3 bg-purple-50 rounded-lg mt-2">
                                <p className="text-sm text-purple-800">
                                  <span className="font-medium">Pickup Location:</span> {invoice.pickup_location}
                                </p>
                              </div>
                            )}
                            
                            {/* Delivery Notes */}
                            {isDelivery && invoice.delivery_notes && (
                              <div className="p-3 bg-green-50 rounded-lg mt-2">
                                <p className="text-sm text-green-800">
                                  <span className="font-medium">Delivery Notes:</span> {invoice.delivery_notes}
                                </p>
                              </div>
                            )}
                            
                            {/* Timeline */}
                            {(invoice.production_started_at || invoice.shipped_at || invoice.delivered_at) && (
                              <div className="mt-4 pt-4 border-t border-neutral-200">
                                <p className="text-xs font-medium text-neutral-500 mb-2">Timeline</p>
                                <div className="space-y-1 text-sm">
                                  {invoice.delivered_at && (
                                    <p className="text-green-600">
                                      ✓ {isPickup ? 'Picked up' : 'Delivered'}: {new Date(invoice.delivered_at).toLocaleDateString()}
                                    </p>
                                  )}
                                  {invoice.shipped_at && (
                                    <p className="text-blue-600">
                                      ✓ {isPickup ? 'Ready for pickup' : 'Shipped'}: {new Date(invoice.shipped_at).toLocaleDateString()}
                                    </p>
                                  )}
                                  {invoice.production_started_at && (
                                    <p className="text-amber-600">
                                      ✓ Production started: {new Date(invoice.production_started_at).toLocaleDateString()}
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </TabsContent>

            {/* Documents Tab */}
            <TabsContent value="documents">
              <div className="bg-white border border-neutral-200 rounded-lg p-6">
                <h2 className="text-lg font-semibold mb-6">Tax & Trade Documents</h2>
                
                <div className="space-y-4">
                  {/* ST-120 Status */}
                  <div className="flex items-center justify-between p-4 border border-neutral-200 rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-amber-700" />
                      </div>
                      <div>
                        <h4 className="font-medium">ST-120 Resale Certificate</h4>
                        <p className="text-sm text-neutral-500">NY State tax exemption form</p>
                        {user.st120_signed && st120Data?.signed_date && (
                          <p className="text-xs text-green-600 mt-1">
                            Signed on {new Date(st120Data.signed_date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                    {user.st120_signed ? (
                      <div className="flex items-center gap-2">
                        <span className="flex items-center gap-2 text-green-600 text-sm font-medium">
                          <Check className="w-4 h-4" /> On File
                        </span>
                        {st120Data && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setSelectedInvoice({ type: 'st120', data: st120Data })}
                          >
                            <Eye className="w-4 h-4 mr-1" /> View
                          </Button>
                        )}
                      </div>
                    ) : (
                      <Link to="/st120">
                        <Button variant="outline" size="sm">Submit Form</Button>
                      </Link>
                    )}
                  </div>

                  {/* Trade Application */}
                  <div className="flex items-center justify-between p-4 border border-neutral-200 rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-neutral-100 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-neutral-700" />
                      </div>
                      <div>
                        <h4 className="font-medium">Trade Account Application</h4>
                        <p className="text-sm text-neutral-500">Apply for professional pricing</p>
                      </div>
                    </div>
                    {user.is_trade ? (
                      <span className="flex items-center gap-2 text-green-600 text-sm font-medium">
                        <Check className="w-4 h-4" /> Approved
                      </span>
                    ) : user.trade_status === 'pending' ? (
                      <span className="text-amber-600 text-sm font-medium">Pending Review</span>
                    ) : (
                      <Link to="/trade">
                        <Button variant="outline" size="sm">Apply Now</Button>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Invoice/Document Preview Modal - Shared across tabs */}
            <AnimatePresence>
                {selectedInvoice && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                    onClick={() => setSelectedInvoice(null)}
                  >
                    <motion.div
                      initial={{ scale: 0.95, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.95, opacity: 0 }}
                      className="bg-white rounded-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {selectedInvoice.type === 'st120' ? (
                        /* ST-120 Preview */
                        <div>
                          <div className="p-6 border-b border-neutral-200 flex items-center justify-between">
                            <div>
                              <h2 className="text-lg font-semibold">ST-120 Resale Certificate</h2>
                              <p className="text-sm text-neutral-500">NY State Tax Exemption Form</p>
                            </div>
                            <button onClick={() => setSelectedInvoice(null)} className="p-2 hover:bg-neutral-100 rounded-lg">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                          <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                              <div className="p-4 bg-neutral-50 rounded-lg">
                                <p className="text-xs text-neutral-500 uppercase">Business Name</p>
                                <p className="font-medium">{selectedInvoice.data.business_name}</p>
                              </div>
                              <div className="p-4 bg-neutral-50 rounded-lg">
                                <p className="text-xs text-neutral-500 uppercase">Tax ID</p>
                                <p className="font-medium">{selectedInvoice.data.tax_id}</p>
                              </div>
                            </div>
                            <div className="p-4 bg-neutral-50 rounded-lg">
                              <p className="text-xs text-neutral-500 uppercase">Business Address</p>
                              <p className="font-medium">{selectedInvoice.data.address}</p>
                              <p>{selectedInvoice.data.city}, {selectedInvoice.data.state} {selectedInvoice.data.zip}</p>
                            </div>
                            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                              <div className="flex items-center gap-2 text-green-700">
                                <CheckCircle className="w-5 h-5" />
                                <span className="font-medium">Electronically Signed</span>
                              </div>
                              <p className="text-sm text-green-600 mt-1">
                                Signed by {selectedInvoice.data.signer_name} on {new Date(selectedInvoice.data.signed_date).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        /* Invoice Preview - Full Format */
                        <div>
                          {/* Header */}
                          <div className="p-6 border-b border-neutral-200">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                                    <Receipt className="w-5 h-5 text-amber-600" />
                                  </div>
                                  <div>
                                    <h2 className="text-xl font-semibold">Invoice {selectedInvoice.invoice_number}</h2>
                                    <p className="text-sm text-neutral-500">
                                      {new Date(selectedInvoice.created_at).toLocaleDateString()}
                                    </p>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`px-3 py-1.5 rounded-full text-sm font-bold ${
                                  selectedInvoice.status === 'paid' ? 'bg-green-100 text-green-700' :
                                  selectedInvoice.status === 'overdue' ? 'bg-red-100 text-red-700' :
                                  selectedInvoice.status === 'partial' ? 'bg-amber-100 text-amber-700' :
                                  'bg-blue-100 text-blue-700'
                                }`}>
                                  {selectedInvoice.status?.toUpperCase()}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 mt-4">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePrintInvoice(selectedInvoice.id)}
                              >
                                <Printer className="w-4 h-4 mr-1" /> Print
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadInvoicePDF(selectedInvoice.id, selectedInvoice.invoice_number)}
                              >
                                <Download className="w-4 h-4 mr-1" /> Download PDF
                              </Button>
                              <button onClick={() => setSelectedInvoice(null)} className="ml-auto p-2 hover:bg-neutral-100 rounded-lg">
                                <X className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                          
                          <div className="p-6 space-y-6">
                            {/* Customer & Billing Info */}
                            <div className="grid grid-cols-2 gap-6">
                              <div className="p-4 bg-neutral-50 rounded-lg">
                                <p className="text-xs text-neutral-500 uppercase tracking-wider mb-2">Bill To</p>
                                <p className="font-medium">{selectedInvoice.customer_name}</p>
                                {selectedInvoice.company_name && (
                                  <p className="text-sm text-neutral-600">{selectedInvoice.company_name}</p>
                                )}
                                <p className="text-sm text-neutral-600">{selectedInvoice.customer_email}</p>
                                {selectedInvoice.billing_address && (
                                  <div className="text-sm text-neutral-600 mt-2">
                                    {typeof selectedInvoice.billing_address === 'object' ? (
                                      <>
                                        <p>{selectedInvoice.billing_address.street}</p>
                                        <p>{selectedInvoice.billing_address.city}, {selectedInvoice.billing_address.state} {selectedInvoice.billing_address.zip}</p>
                                      </>
                                    ) : (
                                      <p>{selectedInvoice.billing_address}</p>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="p-4 bg-neutral-50 rounded-lg">
                                <p className="text-xs text-neutral-500 uppercase tracking-wider mb-2">Invoice Details</p>
                                <div className="space-y-1 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-neutral-500">Invoice #</span>
                                    <span className="font-mono">{selectedInvoice.invoice_number}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-neutral-500">Date</span>
                                    <span>{new Date(selectedInvoice.created_at).toLocaleDateString()}</span>
                                  </div>
                                  {selectedInvoice.due_date && (
                                    <div className="flex justify-between">
                                      <span className="text-neutral-500">Due Date</span>
                                      <span>{new Date(selectedInvoice.due_date).toLocaleDateString()}</span>
                                    </div>
                                  )}
                                  {selectedInvoice.quote_id && (
                                    <div className="flex justify-between">
                                      <span className="text-neutral-500">Quote Ref</span>
                                      <span className="font-mono">{selectedInvoice.quote_id.slice(0,8).toUpperCase()}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            {/* Items Table */}
                            <div>
                              <h3 className="font-medium mb-3">Items</h3>
                              <div className="border rounded-lg overflow-hidden">
                                <table className="w-full">
                                  <thead className="bg-neutral-100 text-sm">
                                    <tr>
                                      <th className="text-left px-4 py-3 font-medium">Description</th>
                                      <th className="text-left px-4 py-3 font-medium">Specifications</th>
                                      <th className="text-center px-4 py-3 font-medium">Qty</th>
                                      <th className="text-right px-4 py-3 font-medium">Unit Price</th>
                                      <th className="text-right px-4 py-3 font-medium">Amount</th>
                                    </tr>
                                  </thead>
                                  <tbody className="text-sm">
                                    {selectedInvoice.items?.map((item, idx) => {
                                      const config = item.config || {};
                                      const productName = (config.product_type || 'Custom Shade').replace(/-/g, ' ');
                                      const isDrape = productName.toLowerCase().includes('drape');
                                      return (
                                        <tr key={idx} className="border-t">
                                          <td className="px-4 py-3">
                                            <p className="font-medium capitalize">{productName}</p>
                                            {config.room_label && <p className="text-xs text-neutral-500">{config.room_label}</p>}
                                          </td>
                                          <td className="px-4 py-3 text-neutral-600">
                                            <div className="space-y-1">
                                              <p className="font-medium">{config.width}" × {config.height}"</p>
                                              {/* Fabric & Color */}
                                              {config.fabric_material && (
                                                <p className="text-xs">
                                                  <span className="text-neutral-400">Fabric:</span> {config.fabric_material}
                                                </p>
                                              )}
                                              {config.fabric_color && (
                                                <p className="text-xs">
                                                  <span className="text-neutral-400">Color:</span> {config.fabric_color}
                                                </p>
                                              )}
                                              {config.color && !config.fabric_color && (
                                                <p className="text-xs">
                                                  <span className="text-neutral-400">Color:</span> {config.color}
                                                </p>
                                              )}
                                              {/* Drape-specific options */}
                                              {isDrape && config.drape_style && (
                                                <p className="text-xs">
                                                  <span className="text-neutral-400">Style:</span> {config.drape_style.replace(/-/g, ' ')}
                                                </p>
                                              )}
                                              {isDrape && config.pleat_style && (
                                                <p className="text-xs">
                                                  <span className="text-neutral-400">Pleat:</span> {config.pleat_style.replace(/-/g, ' ')}
                                                </p>
                                              )}
                                              {isDrape && config.header_style && (
                                                <p className="text-xs">
                                                  <span className="text-neutral-400">Header:</span> {config.header_style.replace(/-/g, ' ')}
                                                </p>
                                              )}
                                              {/* Control/Operation */}
                                              {config.control_type && (
                                                <p className="text-xs">
                                                  <span className="text-neutral-400">Control:</span> {config.control_type.replace(/-/g, ' ')}
                                                </p>
                                              )}
                                              {config.motor_type && (
                                                <p className="text-xs">
                                                  <span className="text-neutral-400">Motor:</span> {config.motor_type.replace(/-/g, ' ')}
                                                </p>
                                              )}
                                              {/* Mount type */}
                                              {config.mount_type && (
                                                <p className="text-xs">
                                                  <span className="text-neutral-400">Mount:</span> {config.mount_type.replace(/-/g, ' ')}
                                                </p>
                                              )}
                                            </div>
                                          </td>
                                          <td className="px-4 py-3 text-center">{item.quantity}</td>
                                          <td className="px-4 py-3 text-right font-mono">{formatPrice(config.total_price)}</td>
                                          <td className="px-4 py-3 text-right font-mono">{formatPrice(config.total_price)}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                            
                            {/* Totals */}
                            <div className="flex justify-end">
                              <div className="w-72 space-y-2">
                                <div className="flex justify-between text-sm">
                                  <span className="text-neutral-500">Subtotal</span>
                                  <span className="font-mono">{formatPrice(selectedInvoice.subtotal)}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-neutral-500">
                                    Tax {selectedInvoice.tax_exempt && <span className="text-green-600">(Exempt)</span>}
                                  </span>
                                  <span className="font-mono">{formatPrice(selectedInvoice.tax)}</span>
                                </div>
                                <div className="flex justify-between text-lg font-bold pt-2 border-t">
                                  <span>Total</span>
                                  <span className="font-mono">{formatPrice(selectedInvoice.total)}</span>
                                </div>
                                {selectedInvoice.amount_paid > 0 && (
                                  <div className="flex justify-between text-sm text-green-600">
                                    <span>Amount Paid</span>
                                    <span className="font-mono">{formatPrice(selectedInvoice.amount_paid)}</span>
                                  </div>
                                )}
                                {selectedInvoice.balance_due > 0 && selectedInvoice.status !== 'paid' && (
                                  <div className="flex justify-between text-sm font-semibold text-red-600">
                                    <span>Balance Due</span>
                                    <span className="font-mono">{formatPrice(selectedInvoice.balance_due)}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* Production & Fulfillment Status */}
                            {selectedInvoice.status === 'paid' && (
                              <div className="p-4 bg-blue-50 rounded-lg">
                                <p className="text-xs text-blue-600 uppercase tracking-wider mb-2">Order Status</p>
                                <div className="flex items-center gap-4">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-blue-800">Production:</span>
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      selectedInvoice.production_status === 'completed' ? 'bg-green-100 text-green-700' :
                                      selectedInvoice.production_status === 'in_production' ? 'bg-amber-100 text-amber-700' :
                                      'bg-neutral-100 text-neutral-600'
                                    }`}>
                                      {(selectedInvoice.production_status || 'pending').replace('_', ' ').toUpperCase()}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-blue-800">Fulfillment:</span>
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      ['delivered', 'picked_up'].includes(selectedInvoice.fulfillment_status) ? 'bg-green-100 text-green-700' :
                                      ['shipped', 'in_transit', 'ready_for_pickup'].includes(selectedInvoice.fulfillment_status) ? 'bg-blue-100 text-blue-700' :
                                      'bg-neutral-100 text-neutral-600'
                                    }`}>
                                      {(selectedInvoice.fulfillment_status || 'pending').replace('_', ' ').toUpperCase()}
                                    </span>
                                  </div>
                                </div>
                                {selectedInvoice.tracking_number && (
                                  <p className="text-sm text-blue-800 mt-2">
                                    Tracking: <span className="font-mono">{selectedInvoice.tracking_number}</span>
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Pay Button */}
                            {selectedInvoice.status !== 'paid' && selectedInvoice.balance_due > 0 && (
                              <div className="pt-4 border-t">
                                <Link to={`/pay/invoice/${selectedInvoice.id}`} className="block">
                                  <Button className="w-full btn-primary py-3">
                                    <CreditCard className="w-5 h-5 mr-2" />
                                    Pay {formatPrice(selectedInvoice.balance_due)} Now
                                  </Button>
                                </Link>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
          </Tabs>
        </div>
      </section>
      
      {/* Sales Order Change Request Dialog */}
      <ChangeRequestDialog
        open={showSOChangeDialog}
        onOpenChange={setShowSOChangeDialog}
        soNumber={changeRequestSO?.so_number}
        isSubmitting={submittingChangeRequest}
        onSubmit={async (message) => {
          if (!changeRequestSO) return;
          setSubmittingChangeRequest(true);
          try {
            await axios.put(
              `${API_URL}/sales-orders/${changeRequestSO.id}/request-changes`, 
              { message }, 
              { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Change request submitted successfully');
            setShowSOChangeDialog(false);
            setChangeRequestSO(null);
            fetchSalesOrders();
          } catch (err) {
            toast.error('Failed to submit change request');
          } finally {
            setSubmittingChangeRequest(false);
          }
        }}
      />
    </Layout>
  );
};

export default DashboardPage;
